<template>
  <div class="agent-profile-settings">
    <h2>Agent Profile</h2>
    <p class="section-description">
      Standing instructions the agent treats as <strong>authoritative</strong> &mdash; it follows
      these over anything it has learned about you. Use this to set your role, jurisdiction focus,
      preferred sources, and topics to always or never surface.
    </p>

    <div v-if="loading" class="skeleton-container">
      <div v-for="n in 4" :key="n" class="skeleton-item"></div>
    </div>

    <template v-else>
      <!-- Your profile -->
      <div class="settings-card">
        <div class="card-header">
          <i class="fas fa-user-gear"></i>
          <div>
            <h3>Your Profile</h3>
            <p>Applies to your sessions only, layered on top of the organization profile.</p>
          </div>
        </div>

        <form @submit.prevent="saveUserProfile" class="profile-form">
          <div class="form-group">
            <label>Role</label>
            <input
              type="text"
              v-model="userForm.role"
              class="form-control"
              placeholder="e.g. Compliance Manager, Associate Veterinarian"
              maxlength="120"
            />
          </div>

          <div class="form-group">
            <label>Primary jurisdiction(s)</label>
            <input
              type="text"
              v-model="userForm.primary_jurisdictions"
              class="form-control"
              placeholder="Comma separated, e.g. FL, GA"
            />
            <div class="field-hint">Comma separated.</div>
          </div>

          <div class="form-group">
            <label>Service-line / specialty focus</label>
            <input
              type="text"
              v-model="userForm.service_line_focus"
              class="form-control"
              placeholder="Comma separated, e.g. general liability, small animal"
            />
            <div class="field-hint">Comma separated.</div>
          </div>

          <div class="form-group">
            <label>Preferred sources</label>
            <input
              type="text"
              v-model="userForm.source_preferences"
              class="form-control"
              placeholder="Comma separated, e.g. PubMed, Merck Vet Manual"
            />
            <div class="field-hint">Comma separated. Used to rank evidence.</div>
          </div>

          <div class="form-group">
            <label>Always include / prioritize</label>
            <input
              type="text"
              v-model="userForm.always_include"
              class="form-control"
              placeholder="Comma separated topics to always surface"
            />
          </div>

          <div class="form-group">
            <label>Never include / avoid</label>
            <input
              type="text"
              v-model="userForm.never_include"
              class="form-control"
              placeholder="Comma separated topics to suppress"
            />
            <div class="field-hint">Suppresses matching learned memories too.</div>
          </div>

          <div class="form-group">
            <label>Standing notes</label>
            <textarea
              v-model="userForm.free_text_notes"
              class="form-control"
              rows="5"
              maxlength="8000"
              placeholder="Free-form instructions (markdown) the agent should always follow."
            ></textarea>
          </div>

          <div v-if="userError" class="error-note">{{ userError }}</div>
          <div v-if="userSaved" class="success-note"><i class="fas fa-check"></i> Saved</div>

          <button type="submit" class="btn btn-primary" :disabled="savingUser">
            <span v-if="savingUser" class="spinner"></span>
            {{ savingUser ? 'Saving...' : 'Save My Profile' }}
          </button>
        </form>
      </div>

      <!-- Organization profile (admins only) -->
      <div v-if="canEditOrg" class="settings-card">
        <div class="card-header">
          <i class="fas fa-building"></i>
          <div>
            <h3>Organization Profile</h3>
            <p>Baseline standing instructions for everyone in your organization.</p>
          </div>
        </div>

        <form @submit.prevent="saveOrgProfile" class="profile-form">
          <div class="form-group">
            <label>Primary jurisdiction(s)</label>
            <input type="text" v-model="orgForm.primary_jurisdictions" class="form-control" placeholder="Comma separated" />
          </div>
          <div class="form-group">
            <label>Service-line focus</label>
            <input type="text" v-model="orgForm.service_line_focus" class="form-control" placeholder="Comma separated" />
          </div>
          <div class="form-group">
            <label>Preferred sources</label>
            <input type="text" v-model="orgForm.source_preferences" class="form-control" placeholder="Comma separated" />
          </div>
          <div class="form-group">
            <label>Always include</label>
            <input type="text" v-model="orgForm.always_include" class="form-control" placeholder="Comma separated" />
          </div>
          <div class="form-group">
            <label>Never include</label>
            <input type="text" v-model="orgForm.never_include" class="form-control" placeholder="Comma separated" />
          </div>
          <div class="form-group">
            <label>Standing notes</label>
            <textarea v-model="orgForm.free_text_notes" class="form-control" rows="4" maxlength="8000"></textarea>
          </div>

          <div v-if="orgError" class="error-note">{{ orgError }}</div>
          <div v-if="orgSaved" class="success-note"><i class="fas fa-check"></i> Saved</div>

          <button type="submit" class="btn btn-secondary" :disabled="savingOrg">
            <span v-if="savingOrg" class="spinner"></span>
            {{ savingOrg ? 'Saving...' : 'Save Org Profile' }}
          </button>
        </form>
      </div>
    </template>
  </div>
</template>

<script>
import api from '@/services/api';

const LIST_FIELDS = [
  'primary_jurisdictions',
  'service_line_focus',
  'always_include',
  'never_include',
  'source_preferences',
];

function emptyForm() {
  return {
    role: '',
    primary_jurisdictions: '',
    service_line_focus: '',
    always_include: '',
    never_include: '',
    source_preferences: '',
    free_text_notes: '',
  };
}

/** Convert API arrays -> comma string for the text inputs. */
function fromProfile(profile) {
  const form = emptyForm();
  if (!profile) return form;
  form.role = profile.role || '';
  form.free_text_notes = profile.free_text_notes || '';
  LIST_FIELDS.forEach((f) => {
    form[f] = (profile[f] || []).join(', ');
  });
  return form;
}

/** Convert comma string -> array payload for the API. */
function toPayload(form, includeRole) {
  const payload = { free_text_notes: form.free_text_notes || '' };
  if (includeRole) payload.role = form.role || '';
  LIST_FIELDS.forEach((f) => {
    payload[f] = (form[f] || '')
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  });
  return payload;
}

export default {
  name: 'AgentProfileSettings',

  data() {
    return {
      loading: true,
      userForm: emptyForm(),
      orgForm: emptyForm(),
      canEditOrg: false,
      savingUser: false,
      savingOrg: false,
      userError: '',
      orgError: '',
      userSaved: false,
      orgSaved: false,
    };
  },

  async mounted() {
    await this.loadProfiles();
  },

  methods: {
    async loadProfiles() {
      this.loading = true;
      try {
        const userResp = await api.get('/agent/profile/');
        this.userForm = fromProfile(userResp.data.profile);

        const orgResp = await api.get('/agent/profile/org/');
        this.canEditOrg = !!orgResp.data.can_edit;
        if (this.canEditOrg) {
          this.orgForm = fromProfile(orgResp.data.profile);
        }
      } catch (error) {
        this.userError = this.errMsg(error);
      } finally {
        this.loading = false;
      }
    },

    async saveUserProfile() {
      this.savingUser = true;
      this.userError = '';
      this.userSaved = false;
      try {
        await api.put('/agent/profile/', toPayload(this.userForm, true));
        this.userSaved = true;
        setTimeout(() => { this.userSaved = false; }, 3000);
      } catch (error) {
        this.userError = this.errMsg(error);
      } finally {
        this.savingUser = false;
      }
    },

    async saveOrgProfile() {
      this.savingOrg = true;
      this.orgError = '';
      this.orgSaved = false;
      try {
        await api.put('/agent/profile/org/', toPayload(this.orgForm, false));
        this.orgSaved = true;
        setTimeout(() => { this.orgSaved = false; }, 3000);
      } catch (error) {
        this.orgError = this.errMsg(error);
      } finally {
        this.savingOrg = false;
      }
    },

    errMsg(error) {
      return error?.response?.data?.error || error?.message || 'Something went wrong';
    },
  },
};
</script>

<style scoped>
.agent-profile-settings {
  max-width: 760px;
}
.section-description {
  color: var(--text-secondary, #6b7280);
  margin-bottom: 20px;
}
.settings-card {
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 24px;
}
.card-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 20px;
}
.card-header i {
  font-size: 22px;
  color: var(--primary-color, #4f46e5);
  margin-top: 2px;
}
.card-header h3 {
  margin: 0 0 4px;
}
.card-header p {
  margin: 0;
  color: var(--text-secondary, #6b7280);
  font-size: 14px;
}
.form-group {
  margin-bottom: 16px;
}
.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 6px;
}
.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
}
.field-hint {
  font-size: 12px;
  color: var(--text-secondary, #9ca3af);
  margin-top: 4px;
}
.btn {
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
}
.btn-primary {
  background: var(--primary-color, #4f46e5);
  color: #fff;
}
.btn-secondary {
  background: var(--secondary-bg, #f3f4f6);
  color: var(--text-primary, #111827);
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.error-note {
  color: #dc2626;
  margin-bottom: 12px;
}
.success-note {
  color: #16a34a;
  margin-bottom: 12px;
}
.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-right: 6px;
  vertical-align: middle;
}
.skeleton-container {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.skeleton-item {
  height: 60px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
</style>
